import type { KeyValue } from '@angular/common';
import { KeyValuePipe } from '@angular/common';
import { Component, computed, ElementRef, inject, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';

import { LanguageService, type StorageFile, type StorageFileProperties } from '@evc/platform';
import {
  ButtonComponent,
  IconComponent,
  icons,
  Maybe,
  StatusIndicatorComponent,
} from '@evc/web-components';
import { I18nService, TranslatePipe } from '@shared/services/i18n/i18n.service';
import * as dateUtils from '@shared/utils/date.utils';

import { FileItemComponent } from '@app/components/file-item/file-item.component';
import { PanelService } from '@app/services';
import type { AppsState } from '@app/store/reducers';
import { getSelectedItems } from '@app/store/selectors';

export type MetadataItem = {
  name: string;
  value: string;
  icon: string;
};
export type MetadataGroups = Record<'application' | 'document', MetadataItem[]>;
type FileMetadataKey = keyof StorageFileProperties['file'];

@Component({
  selector: 'right-panel',
  templateUrl: './right-panel.component.html',
  styleUrls: ['./right-panel.component.scss'],
  imports: [
    ButtonComponent,
    FileItemComponent,
    StatusIndicatorComponent,
    IconComponent,
    TranslatePipe,
    KeyValuePipe,
  ],
})
export class RightPanelComponent {
  readonly #documentMetadataKeys: FileMetadataKey[] = [
    'creationDate',
    'modificationDate',
    'formatVersion',
    'notice',
  ];
  readonly #applicationMetadataKeys: FileMetadataKey[] = [
    'createdByAppName',
    'createdByAppVersion',
  ];
  readonly itemNameElement = viewChild.required<ElementRef<HTMLElement>>('itemName');
  readonly #i18nService: I18nService = inject(I18nService);
  readonly #languageService = inject(LanguageService);
  readonly #store: Store<AppsState> = inject(Store);
  readonly #panelService = inject(PanelService);

  protected selectedItems = toSignal<StorageFile[]>(this.#store.pipe(select(getSelectedItems)));
  protected currentItem = computed<Maybe<StorageFile>>(() => this.selectedItems()?.[0]);
  protected metadatas = computed<MetadataGroups>(() => this.#computeMetadatas(this.currentItem()));
  protected readonly metadataGroupOrder = (
    document: KeyValue<keyof MetadataGroups, MetadataItem[]>,
    application: KeyValue<keyof MetadataGroups, MetadataItem[]>,
  ): number => {
    const order: Record<keyof MetadataGroups, number> = {
      document: 0,
      application: 1,
    };

    return order[document.key] - order[application.key];
  };
  protected selectionLabel = computed<string>(() =>
    this.#i18nService.t('labels.nSelectedItems', { p11n: this.selectedItems()?.length }),
  );

  protected close(): void {
    this.#panelService.toggleRightPanelVisibility(false);
  }

  #isMetadataEmpty(metadata?: StorageFileProperties): boolean {
    return !metadata || Object.keys(metadata).length === 0;
  }

  #computeMetadatas(item?: StorageFile): MetadataGroups {
    const metadataGroups: MetadataGroups = {
      document: [],
      application: [],
    };
    const metadata = item?.latestVersion?.ndeMetadata?.properties;

    if (this.#isMetadataEmpty(metadata)) {
      metadataGroups.document.push(
        this.#defaultMetadataValue({
          name: 'createdAt',
          value: this.#parseDate(item?.createdAt ?? ''),
        }),
        this.#defaultMetadataValue({
          name: 'updatedAt',
          value: this.#parseDate(item?.updatedAt ?? ''),
        }),
      );
    } else {
      metadataGroups.document = this.#documentMetadataKeys.map((key) =>
        this.#defaultMetadataValue({
          name: key,
          value: this.#formatMetadataValue(key, metadata?.file?.[key]),
        }),
      );
      metadataGroups.application = this.#applicationMetadataKeys.map((key) =>
        this.#defaultMetadataValue({
          name: key,
          value: metadata?.file?.[key] ?? undefined,
        }),
      );
    }

    if (item?.deletedAt) {
      metadataGroups.document.push(
        this.#defaultMetadataValue({
          name: 'deletedAt',
          value: this.#parseDate(item.deletedAt),
        }),
        this.#defaultMetadataValue({
          name: 'parentNameUponDeletion',
          value: item?.parentNameUponDeletion ?? this.#i18nService.t('pages.drive'),
        }),
      );
    }

    return metadataGroups;
  }

  #formatMetadataValue(name: FileMetadataKey, value: string | null | undefined): Maybe<string> {
    switch (name) {
      case 'creationDate':
      case 'modificationDate':
        return this.#parseDate(value ?? '');
      default:
        return value ?? undefined;
    }
  }

  #defaultMetadataValue(metadata: Partial<MetadataItem> & { name: string }): MetadataItem {
    const { name } = metadata;
    const t = this.#i18nService.t.bind(this.#i18nService);

    const [translatedName, icon] = (() => {
      switch (name) {
        case 'createdAt':
          return [t('info.document.dates.created'), icons.DATE_CREATED];
        case 'creationDate':
          return [t('info.document.dates.created'), icons.DATE_CREATED];
        case 'updatedAt':
          return [t('info.document.dates.updated'), icons.DATE_MODIFIED];
        case 'modificationDate':
          return [t('info.document.dates.updated'), icons.DATE_MODIFIED];
        case 'deletedAt':
          return [t('info.document.dates.deleted'), icons.TRASH];
        case 'formatVersion':
          return [t('info.document.version'), icons.DOC_VERSION];
        case 'notice':
          return [t('info.document.notice'), icons.INFO_LINE];
        case 'createdByAppName':
          return [t('info.application.name'), icons.APP_GENERIC];
        case 'createdByAppVersion':
          return [t('info.application.version'), icons.NUMBER_KEY];
        case 'parentNameUponDeletion':
          return [t('list.headers.parentName.label'), icons.FOLDER];
        default:
          return [name, 'placeholder'];
      }
    })();

    return {
      ...metadata,
      name: translatedName,
      icon,
      value: metadata.value ?? ' - ',
    };
  }

  #parseDate(date: string | Date): Maybe<string> {
    if (!date) return undefined;

    return dateUtils.formatDateWithTime(dateUtils.toDate(date), this.#languageService.locale());
  }
}
